import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { heroMovies } from "@/mocks/movies";

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const total = heroMovies.length;

  const goTo = useCallback((index: number) => {
    setCurrent(((index % total) + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [total]);

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      {heroMovies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={movie.coverImage}
            alt={movie.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-950"></div>
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-end pb-10 md:pb-14">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {heroMovies[current].tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30"
                >
                  {tag}
                </span>
              ))}
              <span className="text-sm text-foreground-300 ml-2">
                {heroMovies[current].year}
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-50 mb-2">
              {heroMovies[current].title}
            </h2>
            <p className="text-sm md:text-base text-foreground-300 mb-4 max-w-2xl line-clamp-2">
              {heroMovies[current].description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/article/article-${(current % 6) + 1}`}
                className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看详情
              </Link>
              <span className="flex items-center gap-1.5 text-sm text-foreground-400">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-primary-500 text-xs"></i>
                </div>
                {heroMovies[current].rating}
              </span>
              <span className="text-sm text-foreground-500">
                {heroMovies[current].director} 导演
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-6">
            {heroMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                  index === current
                    ? "w-8 bg-primary-500"
                    : "w-4 bg-foreground-500/40 hover:bg-foreground-400/60"
                }`}
                aria-label={`切换到第 ${index + 1} 张`}
              />
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={() => goTo(current - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-950/60 text-foreground-200 hover:bg-background-950/80 hover:text-foreground-50 transition-all cursor-pointer whitespace-nowrap"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={() => goTo(current + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-950/60 text-foreground-200 hover:bg-background-950/80 hover:text-foreground-50 transition-all cursor-pointer whitespace-nowrap"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}